import React, { memo, useCallback } from 'react'
import { ControlledHiddenInput } from 'transfix-form/components'
import ControlledSwitch from 'transfix-form/components/ControlledSwitch'
import { TEST_IDS } from 'transfix-form/forms/SavedLaneForm/constants'
import { useSavedLaneFormContext } from 'transfix-form/forms/SavedLaneForm/context/SavedLaneFormContext'
import { TRecipientSchema } from 'transfix-form/forms/SavedLaneForm/schema/RecipientSchema'
import BellOnIcon from 'transfix-icons/build/BellOnIcon'
import DeleteIcon from 'transfix-icons/build/DeleteIcon'
import { IconButton } from 'transfix-ui/components/IconButton'
import Typography from 'transfix-ui/core/Typography'
import { useRecipientRowStyles } from './useRecipientRowStyles'

export interface IRecipientRowProps {
  /**
   * The index value for the recipient in recipients array
   */
  recipientsIndex: number
  /**
   * The user associated to the recipient object
   */
  recipient: Partial<TRecipientSchema>
  /**
   * Function called when user clicks remove stop button
   */
  onRemove: (index: number) => void
}

const RecipientRow = memo<IRecipientRowProps>(({ onRemove, recipient, recipientsIndex }) => {
  const classes = useRecipientRowStyles()
  const parentKey = `recipients[${recipientsIndex}]`

  const { receiveNotifications, user } = recipient || {}
  const { email: userEmail, name: userName } = user || {}

  /**
   * Form Context
   */
  const { control } = useSavedLaneFormContext()

  /**
   * Handler for removing recipient
   */
  const removeRecipientHandler = useCallback(() => {
    onRemove(recipientsIndex)
  }, [onRemove, recipientsIndex])

  return (
    <div className={classes.recipientRow} data-test={TEST_IDS.recipientRow}>
      <div className={classes.recipientName} data-test={TEST_IDS.recipientName}>
        <ControlledHiddenInput control={control} name={`${parentKey}.user`} defaultValue={user} />
        <Typography variant='subtitle2' component='span' data-test='recipient-name'>
          {userName}
        </Typography>
        <Typography variant='subtitle2' component='span' className={classes.separator}>
          &middot;
        </Typography>
        <Typography variant='subtitle2' component='span'>
          {userEmail}
        </Typography>
      </div>
      <div className={classes.settings}>
        <div className={classes.iconWrapper}>
          <BellOnIcon size='large' />
        </div>
        <div className={classes.labelWrapper}>
          <Typography variant='subtitle2'>Receive Notifications</Typography>
          <Typography variant='caption' color='textSecondary'>
            Max 3 emails per day
          </Typography>
        </div>
        <ControlledSwitch
          control={control}
          name={`${parentKey}.receiveNotifications`}
          defaultValue={receiveNotifications}
          SwitchProps={{
            dataTest: TEST_IDS.recipientNotificationSwitch,
          }}
        />
        <IconButton
          Icon={DeleteIcon}
          onClick={removeRecipientHandler}
          size='large'
          data-test={TEST_IDS.recipientDeleteButton}
        />
      </div>
    </div>
  )
})

export default RecipientRow
